import { Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { CommercialUser } from "../shared/models/commercial-user.model";
import { AuthService } from "@auth0/auth0-angular";
import { combineLatest, forkJoin, map, Observable, tap } from "rxjs";
import { jwtDecode } from "jwt-decode";
import { DecodedToken } from "../shared/interfaces/decoded-token.interface";
import { OrlUserservice } from "./backend-services/orl-plan-services/orl-plan-user.service";
import { OrlRoleService } from "./backend-services/orl-plan-services/orl-role-service";
import { Router } from "@angular/router";
import { BeaconService } from "./beacon.service";
import { environment } from "../../environments/environment";

@Injectable({
    providedIn: 'root',
})
export class CommercialService {
    private readonly _user = signal<CommercialUser | null>(null);
    readonly user = this._user.asReadonly();
    private readonly user$$ = toObservable(this._user);

    constructor(
        private auth: AuthService,
        private userService: OrlUserservice,
        private roleService: OrlRoleService,
        private router: Router,
         private beaconService: BeaconService
    ) {
        this.initializeUser();
    }

    private initializeUser(): void {
        if (environment.outage) {
            this.router.navigate(['outage']);
            return;
        }
        const token$ = this.auth.getAccessTokenSilently().pipe(
            map(token => {
                if (token) {
                    const decodedToken: any = this.decodeToken(token);
                    console.log('CommercialService:decodedToken:=', decodedToken);
                    return {
                        scope: decodedToken.scope,
                        token
                    };
                }
                return { applications: '', scope: '', token: null };
            })
        );
        const user$ = this.auth.user$.pipe(
            map(user => {
                console.log('CommercialService:Auth0User:=', user);

                return ({
                    id: user['user_uid'],
                    name: user?.name,
                    userName: user['username'],
                    email: user?.email,
                    company_id: user['company_id'],
                    company_name: user['company_name'],
                    applications: user['applications'],
                    isStaff: Boolean(user['is_staff']),
                    picture: user.picture
                })
            })
        );

        combineLatest([token$, user$]).pipe(
            map(([tokenData, user]) => {
                console.log('CommercialService:tokenData:=', tokenData);
                console.log('CommercialService:user:=', user);
                if (user) {
                    return new CommercialUser(
                        user.id,
                        user.name,
                        user.userName,
                        user.email,
                        user.company_id,
                        user.company_name,
                        user.applications?.split(' ') ?? [],
                        tokenData.scope,
                        tokenData.scope.split(' '),
                        user.isStaff ? 'Internal' : 'External',
                        user.picture
                    );
                }

              
                return null;
            }),
            tap(user$ => {
                if (!user$.commercialAllowed() || !user$.commercialScopeName()) {
                    console.log('No access to commercial application or with minimum scopes');
                    this.router.navigate(['unauthorised']);
                    return;
                }
                this.loadCurrentUserAndRoles(user$);
                if (user$ && !this._user()) {
                    this._user.set(user$);
                }
            })
        ).subscribe(result => {
            console.debug('initializeUser:=', result);
            result.applications
        });
    }

    public getToken(): Observable<string> {
        return this.auth.getAccessTokenSilently();
    }

    public getUser(): Observable<CommercialUser | null> {
        return this.user$$;
    }

    private decodeToken(token: string): DecodedToken {
        try {
            return jwtDecode<DecodedToken>(token);
        } catch (error) {
            console.error('Error decoding token:', error);
            throw new Error('Invalid token');
        }
    }

    private loadCurrentUserAndRoles(data: any) {
        console.log(data, 'data for user');
 this.beaconService.identifyUser(data);
        forkJoin({
            roles: this.roleService.getAllroles(),
            currentUser: this.userService.getUserById(data?.id),
        }).subscribe(({ roles, currentUser }) => {
            // Step 1: Extract scopes starting with 'commercial:'
            const commercialScopes = (data?.scopes || []).filter((scope: string) =>
                scope.startsWith('commercial:')
            );

             
            // Step 2: Get role IDs where role.scopeName matches any commercialScope
            const matchingRoleIds = roles
                .filter((role: any) => commercialScopes.includes(role.scopeName))
                .map((role: any) => role.id);

            if (!currentUser) {
                // User doesn't exist, create new
                this.createUser(data, matchingRoleIds);
            } else {
                this.updateUserDetails(data?.id,data)
                this.updateUserLastLogin(data?.id);
                console.log(matchingRoleIds, 'roles to add')
                if (matchingRoleIds.length > 0) {
                    roles = {
                        roleIds: matchingRoleIds
                    }
                    this.addUserRoles(data?.id,roles);
                }
            }
        });
    }



    private createUser(data: any, roleId: string) {
        const newUser = {
            "id": data?.id,
            "name": data?.name,
            "email": data?.email,
            "userType": data?.userType,
            "roles": [roleId],
            "companyId": parseInt(data?.companyId || '0', 10),
            "isActive": true
        }
        this.userService.createUser(newUser).subscribe(result => {
            console.log(result);
        });
    }

    private updateUserLastLogin(userId: string) {
        this.userService.login(userId).subscribe();
    }
 private updateUserDetails(userId: string,data:any) {
        const newUser = {
            "id": data?.id,
            "name": data?.name,
            "email": data?.email,
            "userType": data?.userType,
            "companyId": parseInt(data?.companyId || '0', 10),
            "isActive": true
        }
        this.userService.updateUser(userId,newUser).subscribe();
    }
    private findRoleIdByScope(roles: any, scopes: any[]) {
        return roles.find(role => scopes?.includes(role.scopeName))?.id
    }

    private addUserRoles(userId: string, roles: any) {
        this.userService.addUserRoles(userId, roles).subscribe();
    }

}
